import type { CountryCode, PhoneNumber } from 'libphonenumber-js/max';
import parsePhoneNumber, { getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js/max';
import { z } from 'zod';

const removeSpaces = (str: string) => str.replace(/\s/g, '');

const getParse = (phoneNumber: string, defaultCountry = 'DE', extract = false) =>
    parsePhoneNumber(removeSpaces(phoneNumber), {
        defaultCountry: defaultCountry as CountryCode,
        extract,
    });

// NOTE: We use isPossible() instead of isValid() because isValid() is too strict and does not allow for invalid phone numbers that are possible
const validatePhoneNumber = (parsed: PhoneNumber | undefined) => parsed != null && parsed.isPossible() === true;

export const validateE164 = (value: string) => validatePhoneNumber(getParse(value));
export const Phone = z.string().min(1).refine(validateE164);

// Stricter than Phone (isValid) and stores E.164, for user management input.
export const UserPhone = z
    .string()
    .min(1)
    .transform((value, ctx) => {
        const parsed = getParse(value);
        if (parsed?.isValid() !== true) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid phone number' });
            return z.NEVER;
        }
        return parsed.format('E.164');
    });

// Broader than removeSpaces (used by Phone/validateE164): quirk prefix detection must
// ignore dashes/parens/dots too. Pre-existing divergence — Phone still space-only.
const STRIP_FORMATTING = /[\s\-().]/g;

const tryZeroCountryCodeQuirk = (phone: string, defaultCountry: CountryCode): PhoneNumber | undefined => {
    if (!isSupportedCountry(defaultCountry)) return undefined;

    const callingCode = getCountryCallingCode(defaultCountry);
    const stripped = phone.replace(STRIP_FORMATTING, '');
    const quirkPrefix = `0${callingCode}`;
    if (!stripped.startsWith(quirkPrefix)) return undefined;

    return parsePhoneNumber(`+${callingCode}${stripped.slice(quirkPrefix.length)}`, {
        defaultCountry,
    });
};

export const normalizePhone = (phone: string, defaultCountry: CountryCode = 'DE'): string | null => {
    const asIs = parsePhoneNumber(phone, { defaultCountry });
    const fixed = tryZeroCountryCodeQuirk(phone, defaultCountry);

    const useFixed =
        fixed != null &&
        (fixed.isValid()
            ? asIs?.isValid() !== true || fixed.getType() === 'MOBILE'
            : asIs?.isValid() !== true && fixed.isPossible());

    const parsed = useFixed ? fixed : asIs;
    if (!parsed?.isPossible()) return null;
    return parsed.format('E.164');
};
